/**
 * V2 Discover — data access. Split out of discover.js on 2026-07-25 (843
 * lines against the plan's ~400 budget). The four reads behind the feed,
 * people search and map, unchanged. discover.js re-exports them all, so
 * importers and tests are unaffected.
 */
import * as gateway from '../../core/gateway.js';
import { cached } from '../../core/query.js';
import { FEED_SELECT, FEED_PAGE_SIZE } from '../../ui/card.js';
// Personal yoinks (shelved under a user's own category) stay off the
// public discovery surfaces — feed, search and map all go through the
// filtered reader (docs/JOURNAL-PLAN.md §11).
import { queryPublicYoinks } from '../journal/journal.js';

/**
 * Fetch one feed page using keyset (cursor) pagination, not offset:
 * "rows older than the cursor" is a single index seek regardless of how
 * deep the reader has scrolled, where offset N forces the database to
 * walk N rows first. The cursor is (created_at, id) — id breaks ties
 * between yoinks logged in the same millisecond.
 */
export async function fetchFeedPage({ categoryId = null, cursor = null, search = '', bounds = null } = {}) {
  const boundsKey = bounds ? `${bounds.north},${bounds.south},${bounds.east},${bounds.west}` : '-';
  const key = `feed:${categoryId || 'all'}:${search || '-'}:${boundsKey}:${cursor ? `${cursor.created_at}/${cursor.id}` : 'first'}`;
  return cached(key, async () => {
    const filters = {};
    if (categoryId) filters.category_id = `eq.${categoryId}`;
    if (bounds) {
      // Geographic scoping (V1 discovery.js:152-154): the grid, not just
      // the map, reads within bounds — near-you feed, and the map's
      // viewport carries back into the cards (M8.5 Tier 1)
      filters['locations.and'] =
        `(lat.gte.${bounds.south},lat.lte.${bounds.north},lng.gte.${bounds.west},lng.lte.${bounds.east})`;
    }
    // PostgREST reserved characters would break the or=() syntax
    const q = search.replace(/[,()%_*\\]/g, ' ').trim();
    const searchOr = q ? `(title.ilike.*${q}*,description.ilike.*${q}*)` : null;
    const cursorOr = cursor
      ? `(created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.lt.${cursor.id}))`
      : null;
    if (searchOr && cursorOr) {
      filters.and = `(or${searchOr},or${cursorOr})`;
    } else if (searchOr) {
      filters.or = searchOr;
    } else if (cursorOr) {
      filters.or = cursorOr;
    }
    const { data, error } = await queryPublicYoinks({
      select: FEED_SELECT,
      filters,
      order: 'created_at.desc,id.desc',
      limit: FEED_PAGE_SIZE,
    });
    if (error) throw error;
    return data || [];
  });
}

// A search isn't only yoinks: the same submit also surfaces matching
// PEOPLE and CATEGORIES as quick-jump rows above the grid, with the
// people you follow sorted to the top.
const PEOPLE_LIMIT = 8;

/** Profiles whose username or display name matches the query. */
export async function fetchPeople(search = '') {
  const q = search.replace(/[,()%_*\\]/g, ' ').trim();
  if (!q) return [];
  return cached(`people-search:${q.toLowerCase()}`, async () => {
    const { data, error } = await gateway.rawQuery(gateway.TABLES.PUBLIC_PROFILES, {
      select: 'id,username,full_name,avatar_url',
      filters: { or: `(username.ilike.*${q}*,full_name.ilike.*${q}*)` },
      order: 'username.asc',
      limit: PEOPLE_LIMIT,
    });
    if (error) throw error;
    return data || [];
  });
}

/**
 * Profiles for a list of ids — batched in.() lookups of 100 (one giant
 * list would blow the URL length, admin/locations.js precedent). Feeds
 * the "People you follow" panel; the id list itself lives in the store.
 */
export async function fetchProfilesByIds(ids = []) {
  const rows = [];
  for (let i = 0; i < ids.length; i += 100) {
    const { data, error } = await gateway.rawQuery(gateway.TABLES.PUBLIC_PROFILES, {
      select: 'id,username,full_name,avatar_url',
      filters: { id: `in.(${ids.slice(i, i + 100).join(',')})` },
      order: 'username.asc',
      limit: 100,
    });
    if (error) throw error;
    rows.push(...(data || []));
  }
  return rows;
}

const MAP_LIMIT = 1000; // V1: map mode loads up to 1000 for density

/** One bounded map load — no cursor; the viewport IS the filter (V1). */
export async function fetchMapPage({ categoryId = null, search = '', bounds = null } = {}) {
  const filters = {};
  if (categoryId) filters.category_id = `eq.${categoryId}`;
  const q = search.replace(/[,()%_*\\]/g, ' ').trim();
  if (q) filters.or = `(title.ilike.*${q}*,description.ilike.*${q}*)`;
  if (bounds) {
    // Filter on the embedded (inner-joined) locations, V1's exact shape
    filters['locations.and'] =
      `(lat.gte.${bounds.south},lat.lte.${bounds.north},lng.gte.${bounds.west},lng.lte.${bounds.east})`;
  }
  const { data, error } = await queryPublicYoinks({
    select: FEED_SELECT,
    filters,
    order: 'created_at.desc,id.desc',
    limit: MAP_LIMIT,
  });
  if (error) throw error;
  return data || [];
}
